import { useState } from 'react'
import { useAsync } from '../../components/Async'
import { catalogs, type AgentCatalogSummary } from '../../lib/api'

interface Props {
  disabled: boolean
  /** Passed down rather than fetched again: the editor above has already asked for it. */
  agents: AgentCatalogSummary[]
  onChoose: (templateKey: string, agentKey?: string) => void
}

/**
 * The starting points, offered before the form rather than after it.
 *
 * Almost nobody authors a policy from nothing. The interesting ones — watching an assistant, blocking
 * a prompt that carries wire instructions, capturing the screen when a rule fires — are three or four
 * decisions deep, and a template gets an operator to the decision they actually care about. The
 * templates come from the server so they are built and validated by the same code path as everything
 * else; a template that stopped validating would be refused rather than handed over.
 */
/** The assistants a template supports, named the way an operator sees them in the dropdown. */
function supportedNames(agentKeys: string[], agents: AgentCatalogSummary[]): string {
  const names = agentKeys.map((key) => agents.find((agent) => agent.key === key)?.displayName ?? key)
  if (names.length <= 1) return names[0] ?? 'no assistant in the catalog'
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

export function TemplatePicker({ disabled, agents, onChoose }: Props) {
  const templates = useAsync(() => catalogs.templates(), [])
  const [agentKey, setAgentKey] = useState('')

  if (!templates.value || templates.value.length === 0) return null
  const chosenAgent = agentKey || agents[0]?.key

  return (
    <div className="card" data-testid="template-picker">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>Start from a template</h3>
        <p className="hint">
          Each one creates a draft you can then edit. Nothing reaches the fleet until it is approved
          and published.
        </p>

        {agents.length > 0 && (
          <div className="field">
            <label htmlFor="template-agent">For the templates that watch an assistant</label>
            <select
              id="template-agent"
              value={chosenAgent}
              onChange={(event) => setAgentKey(event.target.value)}
              data-testid="template-agent"
            >
              {agents.map((agent) => (
                <option key={agent.key} value={agent.key}>
                  {agent.displayName} — {agent.vendor}
                </option>
              ))}
            </select>
          </div>
        )}

        <ul className="template-list">
          {templates.value.map((template) => {
            // A template that acts on a subscription plan can only be built for an assistant whose
            // account row has been read. Offering it for the rest produced a draft the server refused,
            // with an error naming a field the operator never chose.
            const unsupported =
              template.requiresAgent &&
              template.agentKeys.length > 0 &&
              chosenAgent !== undefined &&
              !template.agentKeys.includes(chosenAgent)

            return (
              <li key={template.key}>
                <button
                  type="button"
                  className="template-choice"
                  disabled={disabled || unsupported}
                  onClick={() => onChoose(template.key, template.requiresAgent ? chosenAgent : undefined)}
                  data-testid={`template-${template.key}`}
                >
                  <span className="template-name">{template.displayName}</span>
                  <span className="hint">{template.description}</span>
                  <span className="hint">
                    {template.capability} · {template.triggerSummary}
                  </span>
                  {unsupported && (
                    <span className="hint" data-testid={`template-${template.key}-unavailable`}>
                      Not available for {agents.find((agent) => agent.key === chosenAgent)?.displayName ?? chosenAgent}:
                      this one reads the account row, and only {supportedNames(template.agentKeys, agents)} expose one.
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
